import { useState } from 'react';

import { Alert, Button, Group, Loader, Modal, ScrollArea, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

import { useMatchReviews } from '@/hooks/useMatchReviews';
import type { MatchReviewDecision, RemediationReview } from '@/types/match-review';
import { MatchReviewCard } from './MatchReviewCard';

export interface MatchReviewPanelProps {
  reportId: string;
  /** Fired after a successful submit — the page refreshes the report so
   * the gate alert clears and the stage badge updates. */
  onSubmitted?: () => void | Promise<void>;
}

const MODE_LABELS: Record<string, string> = {
  off: 'off',
  low_confidence: 'low-confidence matches',
  all: 'all retained matches',
};

function isUndecided(review: RemediationReview): boolean {
  return review.analystDecision === null && review.appliedAt === null;
}

/** Undecided cards first, each group in the server's order — so a card
 * sinks below the queue the moment it is decided and the next undecided
 * one takes its place under the analyst's cursor. Keys are stable, so the
 * moved card keeps its note and expanded state. */
export function orderForReview(reviews: RemediationReview[]): RemediationReview[] {
  return [...reviews.filter(isUndecided), ...reviews.filter((r) => !isUndecided(r))];
}

/**
 * The analyst gate between remediation and synthesis: every queued match
 * for every subject, with Confirm / Not the subject per record, "accept
 * the AI's decisions" for the rest, and a single Submit that applies the
 * verdicts and lets the report continue.
 */
export function MatchReviewPanel({ reportId, onSubmitted }: Readonly<MatchReviewPanelProps>) {
  const { data, isLoading, isMutating, error, decide, acceptAi, submit } =
    useMatchReviews(reportId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading && !data) {
    return (
      <Group gap="xs" p="md">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Loading queued matches…
        </Text>
      </Group>
    );
  }

  if (error && !data) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load match reviews">
        {error.message}
      </Alert>
    );
  }

  const list = data;
  const subjects = list?.subjects ?? [];
  const totalRows = subjects.reduce((n, s) => n + s.reviews.length, 0);
  const pending = list?.pendingCount ?? 0;
  const undecided = list?.undecidedCount ?? 0;
  const canSubmit = pending > 0 && undecided === 0 && !isMutating;

  const handleDecide =
    (reviewId: string) => async (decision: MatchReviewDecision, note?: string | null) => {
      try {
        await decide(reviewId, decision, note);
      } catch {
        // toasted by the hook
      }
    };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    try {
      await submit();
      await onSubmitted?.();
    } catch {
      // toasted by the hook
    }
  };

  return (
    <Stack gap="xs" style={{ minHeight: 0, flex: 1 }}>
      <Stack gap={4}>
        <Text size="sm">
          {pending > 0 ? (
            <>
              <strong>{pending - undecided}</strong> of <strong>{pending}</strong> queued match
              {pending === 1 ? '' : 'es'} decided
            </>
          ) : totalRows > 0 ? (
            'Every queued match has been applied.'
          ) : (
            'No matches are queued for review.'
          )}
        </Text>
        {list && list.mode !== 'off' && (
          <Text size="xs" c="dimmed">
            Reviewing {MODE_LABELS[list.mode] ?? list.mode}
            {list.mode === 'low_confidence' &&
              ` below ${Math.round(list.threshold * 100)}% match probability`}
            .
          </Text>
        )}
      </Stack>

      {pending > 0 && (
        <Group gap="xs">
          <Button
            size="xs"
            variant="default"
            disabled={undecided === 0 || isMutating}
            onClick={() => {
              void acceptAi().catch(() => undefined);
            }}
            data-testid="accept-ai-decisions"
          >
            Accept AI decisions for the rest
          </Button>
          <Button
            size="xs"
            disabled={!canSubmit}
            loading={isMutating}
            onClick={() => setConfirmOpen(true)}
            data-testid="submit-match-review"
          >
            Submit review
          </Button>
        </Group>
      )}

      <ScrollArea style={{ flex: 1, minHeight: 0 }} offsetScrollbars>
        <Stack gap="md">
          {subjects.map((subject) => (
            <Stack gap="xs" key={subject.subjectId}>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                {subject.subjectName || 'Subject'} · {subject.reviews.length}
              </Text>
              {orderForReview(subject.reviews).map((review) => (
                <MatchReviewCard
                  key={review.id}
                  review={review}
                  disabled={isMutating}
                  onDecide={handleDecide(review.id)}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </ScrollArea>

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Submit match review?"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            Your decisions are applied to this report. Where a decision overrides the AI, the
            subject&apos;s remediation is re-run with your decisions pinned — only the report
            sections are re-derived — and the report then continues to synthesis.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button size="xs" onClick={handleSubmit} data-testid="confirm-submit-match-review">
              Submit
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
